import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import appConfig from "../../data/config";
import { apiFetch } from "../../helpers/apiFetch";
import { setCartFromApi, addToCart, deleteFromCart, decreaseQuantity, deleteAllFromCart, applyValidation, setQuantity, updateItemObservaciones, attachApiId, setGuardadoParaDespues } from "./cart-slice";
import { getPriceWithPromo } from "../../helpers/product";

const authHeader = (token) => ({
    "Content-Type": "application/json",
    Accept: "application/json",
    Authorization: `Bearer ${token}`,
});

const clientParam = (state) => {
    const { user } = state.auth;
    const selectedClient = state.viajante?.selectedClient;

    if (user?.role === 'viajante' && selectedClient) {
        console.log('[CARRITO] Viajante mode - cliente seleccionado:', selectedClient.id, selectedClient.name);
        return `?as_client_id=${selectedClient.id}`;
    }

    if (user?.role === 'viajante' && !selectedClient) {
        console.warn('[CARRITO] ⚠️ Viajante sin cliente seleccionado!');
    }

    return '';
};

export const syncCartOnLogin = createAsyncThunk(
    "cartApi/sync",
    async (_, { getState, dispatch }) => {
        const { token } = getState().auth;
        const { cartItems } = getState().cart;
        if (!token || cartItems.length === 0) return; // No sincronizar si no hay items locales

        const items = cartItems.map(item => ({
            product_code: item.code,
            quantity: item.quantity,
            observaciones: item.observaciones ?? '',
        }));

        const res = await apiFetch(`${appConfig.apiUrl}/carrito/sync`, {
            method: "POST",
            headers: authHeader(token),
            body: JSON.stringify({ items }),
        });

        if (res.ok) {
            const data = await res.json();
            dispatch(setCartFromApi(data.data));
        }
        // Si falla, el carrito Redux queda intacto — no bloquear el login
    }
);

export const fetchCart = createAsyncThunk(
    "cartApi/fetch",
    async (_, { getState, dispatch }) => {
        const { token } = getState().auth;
        if (!token) return;

        const param = clientParam(getState());
        const res = await apiFetch(`${appConfig.apiUrl}/carrito${param}`, {
            headers: authHeader(token),
        });

        if (res.ok) {
            const data = await res.json();
            dispatch(setCartFromApi(data.data));
            dispatch(validateCart());
        }
    }
);

export const addItemToCartApi = createAsyncThunk(
    "cartApi/addItem",
    async (product, { getState, dispatch }) => {
        const state = getState();
        const { token } = state.auth;
        const { user } = state.auth;
        const selectedClient = state.viajante?.selectedClient;

        // Validar ANTES de agregar al carrito
        if (user?.role === 'viajante' && !selectedClient) {
            console.warn('[addItemToCartApi] ⚠️ Viajante sin cliente seleccionado!');
            toast.error('Por favor selecciona un cliente primero');
            return;
        }

        dispatch(addToCart(product)); // actualización inmediata en UI
        toast.success("Agregado al carrito");

        if (!token) return;

        const param = (user?.role === 'viajante' && selectedClient)
            ? `?as_client_id=${selectedClient.id}`
            : '';

        console.log('[addItemToCartApi] Agregando item con param:', param);

        try {
            const res = await apiFetch(`${appConfig.apiUrl}/carrito/items${param}`, {
                method: "POST",
                headers: authHeader(token),
                body: JSON.stringify({
                    product_code: product.code,
                    quantity: product.quantity || 1,
                    observaciones: product.observaciones ?? '',
                }),
            });

            if (!res.ok) {
                const error = await res.json().catch(() => ({}));
                console.error('[addItemToCartApi] Error:', res.status, error);
                toast.error(`Error al guardar en BD: ${error.message || res.statusText}`);
            } else {
                // Antes se hacía dispatch(fetchCart()) — re-sincronizaba TODO el
                // carrito y recreaba el item con otro cartItemId, dejando huérfano
                // cualquier debounce de cantidad en curso (bug "queda en 1"). Ahora
                // solo vinculamos el _api_item_id que devuelve el POST, sin recrear.
                const created = await res.json().catch(() => null);
                const localItem = getState().cart.cartItems.find(i => i.code === product.code);
                if (created?.carrito_item_id && localItem) {
                    dispatch(attachApiId({
                        cartItemId: localItem.cartItemId,
                        apiItemId: created.carrito_item_id,
                        quantity: created.quantity,
                    }));
                }
            }
        } catch (err) {
            console.error('[addItemToCartApi] Error de conexión:', err);
            toast.error('Error al conectar con el servidor');
        }
    }
);

export const removeItemFromCartApi = createAsyncThunk(
    "cartApi/removeItem",
    async ({ cartItemId, apiItemId }, { getState, dispatch }) => {
        dispatch(deleteFromCart(cartItemId));
        toast.error("Producto eliminado del carrito");

        const { token } = getState().auth;
        if (!token || !apiItemId) return;

        const param = clientParam(getState());
        await fetch(`${appConfig.apiUrl}/carrito/items/${apiItemId}${param}`, {
            method: "DELETE",
            headers: authHeader(token),
        }).catch(() => {});
    }
);

export const decreaseItemQuantityApi = createAsyncThunk(
    "cartApi/decreaseQuantity",
    async ({ product, newQuantity }, { getState, dispatch }) => {
        dispatch(decreaseQuantity(product));
        toast.info("Cantidad actualizada");

        const { token } = getState().auth;
        if (!token || !product._api_item_id) return;

        const param = clientParam(getState());
        if (newQuantity <= 0) {
            await fetch(`${appConfig.apiUrl}/carrito/items/${product._api_item_id}${param}`, {
                method: "DELETE",
                headers: authHeader(token),
            }).catch(() => {});
        } else {
            await fetch(`${appConfig.apiUrl}/carrito/items/${product._api_item_id}${param}`, {
                method: "PUT",
                headers: authHeader(token),
                body: JSON.stringify({ quantity: newQuantity }),
            }).catch(() => {});
        }
    }
);

export const setItemQuantityApi = createAsyncThunk(
    "cartApi/setQuantity",
    async ({ product, quantity }, { getState, dispatch }) => {
        dispatch(setQuantity({ cartItemId: product.cartItemId, quantity }));

        const { token } = getState().auth;
        if (!token || !product._api_item_id) return;

        const param = clientParam(getState());
        try {
            await fetch(`${appConfig.apiUrl}/carrito/items/${product._api_item_id}${param}`, {
                method: "PUT",
                headers: authHeader(token),
                body: JSON.stringify({ quantity }),
            });
        } catch {}

        dispatch(validateCart());
    }
);

export const validateCart = createAsyncThunk(
    "cartApi/validate",
    async (_, { getState, dispatch }) => {
        const { token } = getState().auth;
        const { cartItems } = getState().cart;
        // Los items ya guardados para más tarde no se re-validan acá: su
        // destino ya lo decidió el servidor (GET /carrito reconcilia stock
        // en cada fetch). Re-mandarlos generaría el mismo warning de nuevo
        // y activaría applyValidation sobre algo que ya está resuelto.
        const activeItems = cartItems.filter(item => !item.guardado_para_despues);
        if (!token || activeItems.length === 0) return null;

        const items = activeItems.map(item => {
            const basePrice = item.user_price ?? item.price;
            const expectedPrice = getPriceWithPromo(basePrice, item.promociones, item.quantity);
            return {
                product_code: item.code,
                quantity: item.quantity,
                expected_unit_price: expectedPrice ? parseFloat(expectedPrice.toFixed(2)) : undefined,
            };
        });

        const param = clientParam(getState());
        const res = await apiFetch(`${appConfig.apiUrl}/carrito/validate${param}`, {
            method: "POST",
            headers: authHeader(token),
            body: JSON.stringify({ items }),
        });

        if (!res.ok) return null;
        const data = await res.json();
        dispatch(applyValidation(data));
        return data;
    }
);

export const updateItemObservacionesApi = createAsyncThunk(
    "cartApi/updateObservaciones",
    async ({ itemId, observaciones, cartItemId }, { getState, dispatch }) => {
        dispatch(updateItemObservaciones({ cartItemId, observaciones }));

        const { token } = getState().auth;
        if (!token || !itemId) return;

        const param = clientParam(getState());
        try {
            const res = await apiFetch(`${appConfig.apiUrl}/carrito/items/${itemId}${param}`, {
                method: "PUT",
                headers: authHeader(token),
                body: JSON.stringify({ observaciones }),
            });
            if (!res.ok) {
                console.error('[updateItemObservacionesApi] Error:', res.status);
                toast.error("No se pudieron guardar las observaciones");
            }
        } catch (err) {
            console.error('[updateItemObservacionesApi] Error de conexión:', err);
            toast.error("No se pudieron guardar las observaciones");
        }
    }
);

export const reactivateCartItemApi = createAsyncThunk(
    "cartApi/reactivateItem",
    async ({ cartItemId, apiItemId }, { getState, dispatch }) => {
        const previous = getState().cart.cartItems.find(i => i.cartItemId === cartItemId);
        const previousMotivo = previous?.guardado_motivo ?? null;

        // Optimistic: lo sacamos de "guardado para más tarde" ya mismo. Si el
        // PUT falla, lo volvemos a marcar guardado_para_despues=true con su
        // motivo original (no siempre es 'manual', ej. venía de sin_stock).
        dispatch(setGuardadoParaDespues({ cartItemId, value: false }));

        const { token } = getState().auth;
        if (!token || !apiItemId) return;

        const param = clientParam(getState());
        try {
            const res = await apiFetch(`${appConfig.apiUrl}/carrito/items/${apiItemId}${param}`, {
                method: "PUT",
                headers: authHeader(token),
                body: JSON.stringify({ guardado_para_despues: false }),
            });
            if (!res.ok) {
                dispatch(setGuardadoParaDespues({ cartItemId, value: true, motivo: previousMotivo }));
                toast.error("No se pudo mover el producto al carrito. Intentá nuevamente.");
            }
        } catch (err) {
            dispatch(setGuardadoParaDespues({ cartItemId, value: true, motivo: previousMotivo }));
            toast.error("No se pudo mover el producto al carrito. Intentá nuevamente.");
        }
    }
);

export const saveItemForLaterApi = createAsyncThunk(
    "cartApi/saveItemForLater",
    async ({ cartItemId, apiItemId }, { getState, dispatch }) => {
        // Optimistic: lo movemos a "guardado para más tarde" (motivo manual) ya
        // mismo. Si el PUT falla, lo volvemos a poner como item activo.
        dispatch(setGuardadoParaDespues({ cartItemId, value: true, motivo: 'manual' }));

        const { token } = getState().auth;
        if (!token || !apiItemId) return;

        const param = clientParam(getState());
        try {
            const res = await apiFetch(`${appConfig.apiUrl}/carrito/items/${apiItemId}${param}`, {
                method: "PUT",
                headers: authHeader(token),
                body: JSON.stringify({ guardado_para_despues: true }),
            });
            if (!res.ok) {
                dispatch(setGuardadoParaDespues({ cartItemId, value: false }));
                toast.error("No se pudo guardar el producto para más tarde. Intentá nuevamente.");
            }
        } catch (err) {
            dispatch(setGuardadoParaDespues({ cartItemId, value: false }));
            toast.error("No se pudo guardar el producto para más tarde. Intentá nuevamente.");
        }
    }
);

export const clearCartApi = createAsyncThunk(
    "cartApi/clear",
    async (_, { getState, dispatch }) => {
        dispatch(deleteAllFromCart());

        const { token } = getState().auth;
        if (!token) return;

        const param = clientParam(getState());
        await fetch(`${appConfig.apiUrl}/carrito${param}`, {
            method: "DELETE",
            headers: authHeader(token),
        }).catch(() => {});
    }
);
